import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { StatCard } from "../components/StatCard";
import { RiskBadge } from "../components/Badges";
import { Loader, Btn } from "../components/Primitives";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, RadialBarChart, RadialBar, PolarAngleAxis } from "recharts";
import { AlertTriangle, ShieldCheck, Users, TrendingUp, Download, Clock, ArrowRight } from "lucide-react";
import * as Icons from "lucide-react";

function JourneyPill({ title, pct, onClick, testId, icon, hint, disabled }) {
  const val = pct === null || pct === undefined ? null : pct;
  const c = val === null ? "#94A3B8" : val >= 80 ? "#059669" : val >= 60 ? "#0891B2" : val >= 40 ? "#D97706" : "#DC2626";
  const Ico = Icons[icon] || Icons.Circle;
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testId}
      className={`group relative rounded-xl p-3 text-left transition-all duration-200 ${disabled ? "opacity-60 cursor-not-allowed" : "card-hover cursor-pointer"}`}
      style={{ border: "1px solid var(--border)", background: "var(--surface)" }}>
      <div aria-hidden className="absolute top-0 left-0 right-0 h-0.5 rounded-t-xl" style={{ background: `linear-gradient(90deg, ${c}, transparent)` }} />
      <div className="flex items-center gap-2 mb-2">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${c}15` }}>
          <Ico className="w-3.5 h-3.5" style={{ color: c }} />
        </div>
        <div className="text-[10.5px] font-semibold uppercase tracking-wider muted">{title}</div>
      </div>
      <div className="flex items-baseline gap-1">
        <div className="num-display text-2xl tnum" style={{ color: c }}>{val === null ? "—" : val}</div>
        <div className="text-[10.5px] font-medium" style={{ color: c }}>{val === null ? "" : "%"}</div>
      </div>
      <div className="mt-2 h-1 rounded-full overflow-hidden" style={{ background: "var(--bg-2)" }}>
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${val ?? 0}%`, background: c }} />
      </div>
      {hint && <div className="text-[9.5px] muted mt-1">{hint}</div>}
    </button>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/org/dashboard").then((r) => setData(r.data)).catch(() => setData({ scores: null }));
  }, []);

  if (!data) return <Loader />;
  const scores = data.scores || { overall: 0, subscores: {}, counts: {} };
  const subs = Object.entries(scores.subscores || {}).map(([k, v]) => ({ name: k.replace(/_/g, " "), value: v }));
  const scoreColor = scores.overall >= 80 ? "#059669" : scores.overall >= 60 ? "#D97706" : "#DC2626";
  const scoreLabel = scores.overall >= 80 ? "Regulator-ready" : scores.overall >= 60 ? "Improving" : "Attention required";

  const exportExec = async () => {
    const res = await api.get("/reports/executive", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = "executive.pdf"; a.click();
  };

  return (
    <div>
      <PageHeader
        kicker="Compliance Overview"
        title="DPDP Control Plane"
        description="Real-time posture across obligations, breach cases, rights requests, and vendor governance."
        testId="dashboard-page-header"
        actions={<Btn data-testid="dashboard-export-executive" variant="secondary" onClick={exportExec}><Download className="w-4 h-4" /> Executive Report</Btn>}
      />

      {/* Hero KPI grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        {/* Overall score */}
        <div data-testid="hero-score" className="lg:col-span-1 aurora rounded-2xl p-6 card-shadow-lg stagger-in relative overflow-hidden" style={{ border: "1px solid var(--border)", "--stagger": 0 }}>
          <div aria-hidden className="absolute inset-0 pointer-events-none opacity-[0.35]" style={{
              backgroundImage: "radial-gradient(rgba(79,70,229,0.18) 1px, transparent 1px)",
              backgroundSize: "22px 22px",
              maskImage: "radial-gradient(280px 180px at 80% 20%, #000, transparent 70%)",
            }} />
          <div className="relative flex items-center justify-between mb-2">
            <div className="label-cap">Overall DPDP Score</div>
            <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold" style={{ background: `${scoreColor}15`, color: scoreColor }}>
              <span className="w-1.5 h-1.5 rounded-full pulse-ring" style={{ background: scoreColor }} />
              {scoreLabel}
            </div>
          </div>
          <div className="relative flex items-center gap-5">
            <div className="w-28 h-28 relative shrink-0">
              <ResponsiveContainer width="100%" height={112} minWidth={112}>
                <RadialBarChart innerRadius="72%" outerRadius="100%" data={[{ name: "score", value: scores.overall, fill: scoreColor }]} startAngle={90} endAngle={-270}>
                  <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                  <RadialBar background={{ fill: "#EEF1F6" }} dataKey="value" cornerRadius={20} />
                </RadialBarChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="num-display text-3xl leading-none" style={{ color: scoreColor }}>{scores.overall}</div>
                <div className="text-[10px] mono uppercase tracking-widest muted mt-1">of 100</div>
              </div>
            </div>
            <div className="flex-1 space-y-2">
              <div className="text-xs muted">Weighted across 10 obligation areas including notices, consents, rights, retention, security, breach, DPIA, and vendors.</div>
              <button onClick={() => navigate("/app/assessment")} className="text-xs primary font-medium inline-flex items-center gap-1 hover:gap-2 transition-all">
                Take assessment <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>

        {/* KPI cards */}
        <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="stagger-in" style={{ "--stagger": 1 }}>
            <StatCard testId="stat-critical-gaps" label="Critical gaps" value={scores.counts?.critical_gaps || 0} tone={scores.counts?.critical_gaps > 0 ? "danger" : "ok"} icon="AlertTriangle" hint="Immediate action" />
          </div>
          <div className="stagger-in" style={{ "--stagger": 2 }}>
            <StatCard testId="stat-open-breaches" label="Open breaches" value={scores.counts?.open_breaches || 0} tone={scores.counts?.open_breaches > 0 ? "warn" : "ok"} icon="AlertOctagon" hint="Under investigation" />
          </div>
          <div className="stagger-in" style={{ "--stagger": 3 }}>
            <StatCard testId="stat-open-requests" label="Open rights" value={scores.counts?.open_requests || 0} tone={scores.counts?.open_requests > 0 ? "warn" : "ok"} icon="Users" hint="Data Principal" />
          </div>
          <div className="stagger-in" style={{ "--stagger": 4 }}>
            <StatCard testId="stat-high-risk-vendors" label="High-risk vendors" value={scores.counts?.high_risk_vendors || 0} tone={scores.counts?.high_risk_vendors > 0 ? "danger" : "ok"} icon="Truck" hint="Review needed" />
          </div>
        </div>
      </div>

      {/* Sub-score + tasks */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        {/* Compliance journey strip — 5-pillar summary spanning the row */}
        <div className="lg:col-span-3 surface rounded-2xl p-5 card-shadow card-hover stagger-in" style={{ border: "1px solid var(--border)", "--stagger": 5 }}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-cap mb-1">Compliance journey</div>
              <h3 className="text-[15px] font-semibold ink tracking-tight">5-pillar readiness snapshot</h3>
            </div>
            <div className="text-[10.5px] muted">Company Profile · DPDP · Website</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <JourneyPill title="Company Profile" pct={data.company_profile_pct || 0} onClick={() => navigate("/app/company-profile")} testId="journey-company-profile" icon="Building2" />
            <JourneyPill title="DPDP readiness" pct={scores.overall || 0} onClick={() => navigate("/app/assessment")} testId="journey-dpdp" icon="ClipboardCheck" />
            <JourneyPill
              title="Website scan"
              pct={data.latest_website_scan?.score ?? null}
              onClick={() => navigate("/app/website-scanner")}
              testId="journey-website-scan"
              icon="ScanLine"
              hint={data.latest_website_scan?.grade ? `grade ${data.latest_website_scan.grade}` : "not scanned yet"}
            />
          </div>
        </div>

        <div className="lg:col-span-2 surface rounded-2xl p-6 card-shadow card-hover stagger-in" style={{ border: "1px solid var(--border)", "--stagger": 6 }}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-cap mb-1">Obligation coverage</div>
              <h3 className="text-lg font-semibold ink tracking-tight">Sub-score breakdown</h3>
            </div>
            <TrendingUp className="w-5 h-5 muted-2" strokeWidth={1.75} />
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height={256} minWidth={280}>
              <BarChart data={subs} margin={{ top: 10, right: 10, left: -20, bottom: 24 }}>
                <CartesianGrid strokeDasharray="2 4" stroke="#E4E8EF" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10.5, fill: "#5B6478", fontFamily: "Geist" }} angle={-28} textAnchor="end" interval={0} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10.5, fill: "#5B6478", fontFamily: "Geist" }} domain={[0, 100]} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{ background: "white", border: "1px solid #E4E8EF", borderRadius: 8, fontFamily: "Geist", fontSize: 12, boxShadow: "0 4px 6px -1px rgba(0,0,0,0.06)" }}
                  cursor={{ fill: "rgba(79,70,229,0.06)" }}
                />
                <Bar dataKey="value" fill="url(#barGrad)" radius={[6, 6, 0, 0]} />
                <defs>
                  <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#6366F1" />
                    <stop offset="100%" stopColor="#4F46E5" />
                  </linearGradient>
                </defs>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="surface rounded-2xl p-6 card-shadow card-hover stagger-in" style={{ border: "1px solid var(--border)", "--stagger": 6 }}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-cap mb-1">Priority queue</div>
              <h3 className="text-lg font-semibold ink tracking-tight">Open tasks</h3>
            </div>
            <Clock className="w-5 h-5 muted-2" strokeWidth={1.75} />
          </div>
          {(data.recent_tasks || []).length === 0 ? (
            <div className="text-sm muted py-6 text-center">You're all caught up.</div>
          ) : (
            <div className="space-y-2.5">
              {data.recent_tasks.map((t) => (
                <div key={t.id} className="p-3 rounded-lg hover:bg-app-2 transition-colors cursor-pointer" onClick={() => navigate("/app/gaps")}>
                  <div className="flex justify-between gap-2 items-start">
                    <div className="text-sm font-medium ink line-clamp-2">{t.title}</div>
                    <RiskBadge level={t.risk || t.priority} />
                  </div>
                  <div className="text-[11px] muted mt-1.5 flex items-center gap-1.5">
                    <Clock className="w-3 h-3" /> Due {t.due_date?.slice(0, 10) || "—"}
                  </div>
                </div>
              ))}
              <button onClick={() => navigate("/app/gaps")} className="w-full text-xs primary font-medium inline-flex items-center justify-center gap-1 pt-2 hover:gap-2 transition-all">
                View all tasks <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Vendor + coverage */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="surface rounded-2xl p-6 card-shadow card-hover stagger-in" style={{ border: "1px solid var(--border)", "--stagger": 7 }}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-cap mb-1">Vendor governance</div>
              <h3 className="text-lg font-semibold ink tracking-tight">Upcoming reviews</h3>
            </div>
            <ShieldCheck className="w-5 h-5 muted-2" strokeWidth={1.75} />
          </div>
          {(data.upcoming_reviews || []).length === 0 ? (
            <div className="text-sm muted py-6 text-center">No upcoming reviews.</div>
          ) : (
            <div className="space-y-2" data-testid="upcoming-reviews">
              {data.upcoming_reviews.map((v) => (
                <div key={v.id} className="flex items-center justify-between py-2.5" style={{ borderBottom: "1px solid var(--border)" }}>
                  <div>
                    <div className="text-sm font-medium ink">{v.name}</div>
                    <div className="text-[11px] muted mt-0.5">Next review · {v.next_review_date?.slice(0, 10) || "—"}</div>
                  </div>
                  <RiskBadge level={v.risk_rating} />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="surface rounded-2xl p-6 card-shadow card-hover stagger-in" style={{ border: "1px solid var(--border)", "--stagger": 8 }}>
          <div className="label-cap mb-1">Detailed coverage</div>
          <h3 className="text-lg font-semibold ink tracking-tight mb-4">DPDP obligations</h3>
          <div className="space-y-2">
            {Object.entries(scores.subscores || {}).map(([k, v]) => {
              const c = v >= 80 ? "#059669" : v >= 60 ? "#D97706" : "#DC2626";
              return (
                <div key={k} className="flex items-center gap-3">
                  <div className="text-sm ink capitalize flex-1 min-w-0 truncate">{k.replace(/_/g, " ")}</div>
                  <div className="w-24 h-1.5 rounded-full overflow-hidden" style={{ background: "var(--bg-2)" }}>
                    <div className="h-full rounded-full transition-all duration-500" style={{ width: `${v}%`, background: `linear-gradient(90deg, ${c}, ${c}dd)` }} />
                  </div>
                  <div className="w-9 text-right text-xs font-semibold tnum" style={{ color: c }}>{v}%</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
